/* eslint-disable no-nested-ternary */
import {css, useTheme} from '@mui/material';
import {
  ConfigObjectType,
  GetStylesFunction,
  IconHeaderWithSearch,
  LoadingSpinner,
  enumContainer,
  mixins,
  omitNullsy,
  useDefaultScrollTo,
  useLocalStorage,
  useLocationQuery,
  NoDataMessageWithIcon,
} from '@wallaby-ui/core';
import {
  ChartColor,
  HaidiSchema,
  LightChart,
  LightChartLayout,
  LightChartLegend2,
  LightDateRangeFilter,
  LightOrganizationFilter2,
  LightFilterPanel,
  LightSelectFilter,
  LightSelectedFilters,
  LightSelectedFiltersPane,
  UserFilterDto,
  getVerticalBarChartItems,
  useAgens,
  useAtbList,
  useFormatters,
  useOrganizations,
  useSnomedLabels,
  useTableData,
  useTableParams,
  useUserFilters,
  ShortcutEnum,
} from '@wallaby-ui/document';
import {isEqual, sortBy} from 'lodash';
import React, {useCallback, useMemo, useRef, useState} from 'react';
import {Helmet} from 'react-helmet';

import {useAppSelector} from 'src/js/store_hooks';

type RowType = HaidiSchema.components['schemas']['AtbSusceptibilityOverviewDto'];

interface Props {
  config: ConfigObjectType;
}
type PathogenDetailFilters = {
  dateFrom: string | undefined;
  dateTo: string | undefined;
  floatingShortcutName: ShortcutEnum | null;
  pathogenIds: string[];
  atbCodes?: number[];
  specimenTypes: string[];
  organizationIds: string[];
  measureType: string;
};
type FiltersData = {
  pathogenOpen: boolean;
  pathogen: PathogenDetailFilters;
};
const DEFAULT_PATHOGEN_DETAIL_FILTERS_OPEN = true;
const DEFAULT_PATHOGEN_DETAIL_FILTERS: Partial<PathogenDetailFilters> = {
  measureType: 'ALL_ISOLATES',
};

export const PathogenDetailContainer: React.FC<Props> = (props) => {
  const {config} = props;
  const component = 'pathogenDetail';
  const storageIdPrefix = '/pathogenDetail';
  const [query, changeFilters] = useLocationQuery(storageIdPrefix, true);
  const filters: FiltersData = useMemo(() => {
    return {
      pathogenOpen: query?.pathogenOpen ?? DEFAULT_PATHOGEN_DETAIL_FILTERS_OPEN,
      pathogen: {
        ...DEFAULT_PATHOGEN_DETAIL_FILTERS,
        ...(query?.pathogen ?? {}),
      },
    };
  }, [query]);
  const {waitForUserFilters, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter} = useUserFilters({
    storageIdPrefix,
    component,
    config,
    changeFilters: (newFilters) => changeFilters({pathogen: newFilters}),
  });
  if (waitForUserFilters) return <LoadingSpinner isVisible marginTop={16} />;
  return (
    <PathogenDetailContainerInner
      {...props}
      storageIdPrefix={storageIdPrefix}
      component={component}
      userFiltersLoading={userFiltersLoading}
      addedUserFilters={addedUserFilters}
      userFilters={userFilters}
      changeUserFilter={changeUserFilter}
      filters={filters}
      changeFilters={changeFilters}
    />
  );
};
PathogenDetailContainer.displayName = 'PathogenDetailContainer';

type InnerProps = Props & {
  storageIdPrefix: string;
  component: string;
  userFiltersLoading: boolean;
  addedUserFilters: UserFilterDto[];
  userFilters: UserFilterDto[];
  changeUserFilter: (newUserFilter: UserFilterDto, toDelete?: boolean) => void;
  filters: FiltersData;
  changeFilters: (filterDiff: Record<string, any>) => void;
};
export function normalizePathogenDetailFiltersForCompare(filter: Record<string, any>): Record<string, any> {
  const copy: Record<string, any> = structuredClone(filter ?? {});
  if (copy.floatingShortcutName) {
    delete (copy as any).dateFrom;
    delete (copy as any).dateTo;
  }
  return copy;
}
const PathogenDetailContainerInner: React.FC<InnerProps> = (props) => {
  const {config, storageIdPrefix, component, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter, filters, changeFilters} =
    props;
  const theme = useTheme();
  const muiStyles = getStyles({theme});

  const pathogenDetailUrl = config.service.url.pathogenDetail;
  const pathogenDetailParams = useTableParams({
    dateFrom: filters.pathogen.dateFrom,
    dateTo: filters.pathogen.dateTo,
    floatingShortcutName: filters.pathogen.floatingShortcutName,
    pathogenIds: filters.pathogen.pathogenIds,
    atbSnomedCodes: filters.pathogen.atbCodes,
    specimenTypes: filters.pathogen.specimenTypes,
    organizationIds: filters.pathogen.organizationIds,
    measureType: filters.pathogen.measureType,
  });
  const {data: _data, isLoading: dataIsLoading} = useTableData<any, RowType[]>(pathogenDetailUrl, pathogenDetailParams, 'content');
  const absoluteData = useMemo(() => _data ?? [], [_data]);
  const {snomedLabels, snomedLabelsLoading} = useSnomedLabels(config);

  const formatters = useFormatters();
  const {t, tEnum, formatDate} = formatters;
  const hasFilters = !isEqual(omitNullsy(filters.pathogen), omitNullsy(DEFAULT_PATHOGEN_DETAIL_FILTERS));

  const enumMap = useAppSelector((state) => (state[enumContainer.ENUM_REDUCER_STATE_ID] as any).enumMap);
  const sortByMessageKey = useCallback((options) => sortBy(options, [(v) => t(v.messageKey)]), [t]);
  const {data: organizations} = useOrganizations(config);
  const agensData = useAgens(config);
  const findingOptions = useMemo(() => {
    return sortBy(
      agensData.map((v) => ({id: v.code, label: v.label})),
      (v) => v.label
    );
  }, [agensData]);
  const atbs = useAtbList(config);
  const atbOptions = useMemo(() => {
    return sortBy(
      atbs.map((v) => ({id: v.code, label: v.label})),
      (v) => v.label
    );
  }, [atbs]);
  const isLoading = dataIsLoading || snomedLabelsLoading;
  const baseChartColors: ChartColor[] = useMemo(
    () =>
      [
        {
          id: 'resistantCount',
          label: t('atb.resistence.R'),
          color: '#FF656F',
        },
        {
          id: 'increasedExposureCount',
          label: t('atb.resistence.I'),
          color: '#FF9200',
        },
        {
          id: 'sensitiveCount',
          label: t('atb.resistence.S'),
          color: '#6DDE00',
        },
      ] as ChartColor[],
    [t]
  );
  const chartColorHiddenMapAbsolute = useRef({} as Record<string | number, boolean>);
  const chartColorHiddenMapRelative = useRef({} as Record<string | number, boolean>);
  const [_hiddenMapNonce, _rerender] = useState(0);
  const onSetHiddenMap = useCallback(() => {
    _rerender((v) => v + 1);
  }, [_rerender]);
  const chartColorsAbsolute = useMemo(
    () => {
      return baseChartColors.map((v) => ({...v, hidden: chartColorHiddenMapAbsolute.current[v.id as string]}));
    },
    [baseChartColors, JSON.stringify(chartColorHiddenMapAbsolute.current)] // eslint-disable-line
  );
  const chartColorsRelative = useMemo(
    () => {
      return baseChartColors.map((v) => ({...v, hidden: chartColorHiddenMapRelative.current[v.id as string]}));
    },
    [baseChartColors, JSON.stringify(chartColorHiddenMapRelative.current)] // eslint-disable-line
  );
  const relativeData = useMemo(
    () => {
      return absoluteData.map((v) => {
        let {resistantCount, increasedExposureCount, sensitiveCount} = v;
        if (chartColorHiddenMapRelative.current.resistantCount) resistantCount = 0;
        if (chartColorHiddenMapRelative.current.increasedExposureCount) increasedExposureCount = 0;
        if (chartColorHiddenMapRelative.current.sensitiveCount) sensitiveCount = 0;
        const total = (resistantCount ?? 0) + (increasedExposureCount ?? 0) + (sensitiveCount ?? 0);
        const scaleFactor = 1 / (total || 1);
        return {
          ...v,
          resistantCount: resistantCount * scaleFactor,
          increasedExposureCount: increasedExposureCount * scaleFactor,
          sensitiveCount: sensitiveCount * scaleFactor,
        };
      });
    },
    [absoluteData, JSON.stringify(chartColorHiddenMapRelative.current)] // eslint-disable-line
  );
  const tableFiltersForSelectedPane = useMemo(() => {
    const hasDate = !!filters.pathogen.dateFrom && !!filters.pathogen.dateTo;

    const result: any = {
      ...filters.pathogen,
    };

    if (hasDate) {
      result.date = {
        startDate: filters.pathogen.dateFrom,
        endDate: filters.pathogen.dateTo,
        shortcut: filters.pathogen.floatingShortcutName,
      };
    } else {
      delete result.date;
    }

    return result;
  }, [filters.pathogen]);
  // scroll
  const [defaultScroll, setDefaultScroll] = useLocalStorage(`${storageIdPrefix}.scroll`, 0);
  const scrollWrapperRef = useDefaultScrollTo(defaultScroll);
  // TODO: PDH-1807 fix no data text
  const selectedFiltersPane = (
    <LightSelectedFiltersPane
      selectedFilters={[
        {
          name: ['dateFrom', 'dateTo'],
          label: t('filter.antibiogram.date'),
          value: `${formatDate(filters.pathogen.dateFrom ?? '')} - ${formatDate(filters.pathogen.dateTo ?? '')}`,
        },
        {
          name: 'specimenTypes',
          label: t('filter.antibiogram.specimenTypes'),
          value: (filters.pathogen.specimenTypes ?? []).map((v) => tEnum('LabReportSpecimenTypeEnum', v)).join(', '),
        },
        {
          name: 'measureType',
          label: t('filter.antibiogram.measureType'),
          value: tEnum('AntStewardshipMeasureType', filters.pathogen.measureType),
          defaultValue: DEFAULT_PATHOGEN_DETAIL_FILTERS.measureType,
        },
        {
          name: 'organizationIds',
          label: t('filter.antibiogram.organizationIds'),
          value: (filters.pathogen.organizationIds ?? [])
            .map((id) => organizations.find((w) => String(w.id) === String(id))?.organizationLongName)
            .join(', '),
        },
        {
          name: 'pathogenIds',
          label: t('filter.pathogen'),
          value: (filters.pathogen.pathogenIds ?? [])
            .map((id) => findingOptions.find((w) => String(w.id) === String(id))?.label)
            .join(', '),
        },
        {
          name: 'atbCodes',
          label: t('filter.antibiogram.atbCodes'),
          value: (filters.pathogen.atbCodes ?? [])
            .map((id) => atbOptions.find((w) => String(w.id) === String(id))?.label ?? String(id))
            .join(', '),
        },
      ]}
      tableFilters={tableFiltersForSelectedPane}
      changeTableFilters={(newTableFilters) =>
        changeFilters({
          pathogen: newTableFilters,
        })
      }
      formatters={formatters}
    />
  );
  return (
    <div
      css={muiStyles.contentWrapper}
      ref={scrollWrapperRef}
      onScroll={(event) => {
        setDefaultScroll((event.target as HTMLElement).scrollTop);
      }}
    >
      <Helmet title={`${t('navigation.section.atbReports.browser')} | ${t('navigation.route.atbReports.pathogenDetail')}`} />
      <div css={muiStyles.splitContentBox}>
        <LightFilterPanel
          open={filters.pathogenOpen}
          toggleOpen={() =>
            changeFilters({
              pathogenOpen: !filters.pathogenOpen,
            })
          }
          hasActiveFilter={hasFilters}
          maxScrollAreaHeight='40vh'
          savedFiltersProps={{
            storageIdPrefix,
            component,
            userFiltersLoading,
            addedUserFilters,
            userFilters,
            changeUserFilter,
            filter: filters.pathogen,
            setFilter: (newFilter) => {
              changeFilters({
                pathogen: newFilter,
              });
            },
            config,
            normalizeForCompare: normalizePathogenDetailFiltersForCompare,
          }}
          selectedFilters={selectedFiltersPane}
        >
          <LightDateRangeFilter
            config={{
              name: 'date',
              ui: {label: 'filter.antibiogram.date'},
            }}
            value={
              filters.pathogen.dateFrom && filters.pathogen.dateTo
                ? {
                    startDate: filters.pathogen.dateFrom,
                    endDate: filters.pathogen.dateTo,
                    shortcut: filters.pathogen.floatingShortcutName,
                  }
                : null
            }
            onChange={(newValue) => {
              changeFilters({
                pathogen: {
                  ...filters.pathogen,
                  dateFrom: newValue?.startDate,
                  dateTo: newValue?.endDate,
                  floatingShortcutName: newValue?.shortcut,
                },
              });
            }}
            dateOnly
            showShortcuts
          />
          <LightSelectFilter
            config={{
              name: `specimenTypes`,
              ui: {label: 'filter.antibiogram.specimenTypes'},
            }}
            options={sortByMessageKey(enumMap.LabReportSpecimenTypeEnum.filter((a) => a.name !== 'UNKNOWN'))}
            multi
            value={filters.pathogen.specimenTypes}
            onChange={(newValue) => {
              changeFilters({
                pathogen: {
                  ...filters.pathogen,
                  specimenTypes: newValue,
                },
              });
            }}
          />
          <LightSelectFilter
            config={{
              name: `measureType`,
              ui: {label: 'filter.antibiogram.measureType'},
            }}
            options={sortByMessageKey(enumMap.AntStewardshipMeasureType)}
            value={filters.pathogen.measureType}
            onChange={(newValue) => {
              changeFilters({
                pathogen: {
                  ...filters.pathogen,
                  measureType: newValue,
                },
              });
            }}
            isClearable={false}
          />
          {true && ( // TODO: multihospital?
            <LightOrganizationFilter2
              appConfig={config}
              config={{
                name: 'organizationIds',
                ui: {label: 'filter.antibiogram.organizationIds'},
              }}
              multi
              value={filters.pathogen.organizationIds}
              onChange={(newOrganizationIds) => {
                changeFilters({
                  pathogen: {
                    ...filters.pathogen,
                    organizationIds: newOrganizationIds,
                  },
                });
              }}
            />
          )}
          <LightSelectFilter
            config={{
              name: `pathogenIds`,
              ui: {label: 'filter.pathogen'},
            }}
            options={findingOptions}
            multi
            value={filters.pathogen.pathogenIds}
            onChange={(newValue) => {
              changeFilters({
                pathogen: {
                  ...filters.pathogen,
                  pathogenIds: newValue,
                },
              });
            }}
          />
          <LightSelectFilter
            config={{
              name: `atbCodes`,
              ui: {label: 'filter.antibiogram.atbCodes'},
            }}
            options={atbOptions}
            multi
            value={filters.pathogen.atbCodes}
            onChange={(newValue) => {
              changeFilters({
                pathogen: {
                  ...filters.pathogen,
                  atbCodes: newValue,
                },
              });
            }}
          />
        </LightFilterPanel>
      </div>
      <div data-name='pathogen-detail-wrapper' css={muiStyles.contentBox}>
        <IconHeaderWithSearch
          iconName={'listIconMultiColor'}
          label={'pathogenDetail.charts'}
          useFixedViewBox='0 -1 16 14'
          iconCss={muiStyles.headerIcon}
        >
          <LightSelectedFilters
            hasFilters={hasFilters}
            handleResetFilters={() =>
              changeFilters({
                pathogen: DEFAULT_PATHOGEN_DETAIL_FILTERS,
              })
            }
          />
        </IconHeaderWithSearch>
        {isLoading ? (
          <LoadingSpinner isVisible marginTop={0} />
        ) : (
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              padding: '0 24px',
            }}
          >
            {/* <HorizontalPathogenChart
              chartColors={chartColors}
              data={pathogenDetailData}
              snomedLabels={snomedLabels}
            /> */}
            <VerticalPathogenChart
              label={t('pathogenDetail.charts.absolute')}
              chartColors={chartColorsAbsolute}
              hiddenMap={chartColorHiddenMapAbsolute}
              onSetHiddenMap={onSetHiddenMap}
              data={absoluteData}
              snomedLabels={snomedLabels}
            />
            <VerticalPathogenChart
              label={t('pathogenDetail.charts.relative')}
              chartColors={chartColorsRelative}
              hiddenMap={chartColorHiddenMapRelative}
              onSetHiddenMap={onSetHiddenMap}
              data={relativeData}
              snomedLabels={snomedLabels}
              yIsPercent
            />
          </div>
        )}
      </div>
    </div>
  );
};
type PathogenChartProps = {
  label: string;
  chartColors: ChartColor[];
  hiddenMap: React.MutableRefObject<Record<string | number, boolean>>;
  onSetHiddenMap: () => void;
  data: RowType[];
  snomedLabels: Record<string, string>;
  yIsPercent?: boolean;
};
const VerticalPathogenChart: React.FC<PathogenChartProps> = (props) => {
  const {label, chartColors, hiddenMap, onSetHiddenMap, data, snomedLabels, yIsPercent} = props;
  const allColorsAreHidden = chartColors.every((v) => v.hidden);
  const rangeMax = data.reduce((acc, v) => Math.max(acc, v.resistantCount + v.increasedExposureCount + v.sensitiveCount), 0);
  const CHART_LAYOUT: LightChartLayout = {
    margin: {},
    xaxis: {
      automargin: true,
      tickangle: -90,
      showticklabels: !allColorsAreHidden,
    },
    yaxis: {
      automargin: true,
      fixedrange: true,
      rangemode: 'nonnegative',
      range: [0, rangeMax],
      tickformat: yIsPercent ? '.0%' : undefined,
      nticks: yIsPercent ? 6 : 4,
      tickvals: yIsPercent ? [0, 0.2, 0.4, 0.6, 0.8, 1.0] : undefined,
    },
    showlegend: false,
    barmode: 'stack',
    height: 400,
  };
  const noDataComponent = useMemo(() => <NoDataMessageWithIcon message={'chart.emptyData'} isHaidi />, []);
  return (
    <>
      <IconHeaderWithSearch label={label} style={{marginTop: yIsPercent ? 32 : 0}} labelStyle={{color: 'var(--dlThemeGray-500)'}}>
        {!yIsPercent && <LightChartLegend2 items={chartColors} hiddenMap={hiddenMap} onSetHiddenMap={onSetHiddenMap} />}
      </IconHeaderWithSearch>
      <LightChart
        imageDownload={{
          title: {
            text: label,
          },
          showlegend: true,
        }}
        data={getVerticalBarChartItems(
          sortBy(data, (v) => snomedLabels[v.atbCode]),
          chartColors,
          (v, i) => {
            const x = snomedLabels[v.atbCode] ?? v.atbCode;
            const y = v[chartColors[i].id as string];
            const tooltip = yIsPercent ? `${x}<br><b>${chartColors[i].label}</b>: ${(100 * y).toFixed(0)}%` : undefined;
            return {
              x,
              y,
              tooltip,
            };
          }
        )}
        layout={CHART_LAYOUT}
        emptyWrapperStyle={{minHeight: 100}}
        noDataComponent={noDataComponent}
      />
    </>
  );
};
/*
const HorizontalPathogenChart: React.FC<Omit<PathogenChartProps, 'hiddenMap' | 'onSetHiddenMap'>> = (props) => {
  const {label, data, chartColors, snomedLabels} = props;
  const maxChartHeight = getBarChartHeight(data.length);
  const CHART_LAYOUT: LightChartLayout = {
    margin: {},
    xaxis: {
      nticks: 10,
      automargin: true,
    },
    yaxis: {
      automargin: true,
      fixedrange: true,
    },
    showlegend: false,
    barmode: 'stack',
    height: maxChartHeight,
  };
  return (
    <LightChart
      imageDownload={{
        title: {
          text: label,
        },
        showlegend: true,
      }}
      data={getBarChartItems(
        sortBy(data, (v) => snomedLabels[v.atbCode]),
        chartColors,
        (v, i) => {
          const key = chartColors[i].id as string;
          const x = v[key];
          const y = snomedLabels[v.atbCode] ?? v.atbCode;
          return {
            x,
            y: StringUtils.doEllipsis(y, 15),
          };
        }
      )}
      layout={CHART_LAYOUT}
    />
  );
};
*/

const getStyles: GetStylesFunction = (options) => ({
  contentWrapper: css`
    width: 100%;
    height: 100%;
    padding: 16px 16px 16px 0;
    display: flex;
    flex-direction: column;
    gap: 18px;
    overflow-y: scroll;
  `,
  splitContentBox: css`
    ${mixins.contentBox};
    border-radius: ${options?.theme?.borderRadius};
    padding: ${options?.theme?.spacing(3)};
    background: #ffffff;
    padding: 16px 16px 0 16px;
    row-gap: 16px;
  `,
  contentBox: css`
    ${mixins.contentBox};
    border-radius: ${options?.theme?.borderRadius};
    padding: ${options?.theme?.spacing(3)};
    background: #ffffff;
    padding: 16px;
    row-gap: 16px;
    color: var(--dlTheme-700);
    width: 100%;
  `,
  headerIcon: css`
    width: 16px;
    height: 16px;
  `,
  // legend
  legendWrapper: css`
    display: inline-flex;
    background: var(--dlThemeGray-100);
    border-radius: 8px;
    padding: 8px;
    gap: 8px;
  `,
  legendItem: css`
    padding: 4px 8px;
    border-radius: 5px;
  `,
});
